import { DNADesignElementalModelsBaseElementType, DNADesignElementalModelsElementalAreaType, DNADesignElementalVirtualModelElementVirtualType } from "@/types";
import { ModuleNamespace } from "vite/types/hot.js";
import { createContext, lazy, startTransition, Suspense, useContext, type ComponentType, type LazyExoticComponent } from "react";
import { usePage } from "@inertiajs/react";
import { SharedProps } from "@/components/_components-helper";

const ssrBlocks = import.meta.env.SSR
  ? (import.meta.glob<true, string, ComponentType<unknown>>("@/components/blocks/**/*.tsx", {
      eager: true,
      import: "default",
    }))
  : ({} as Record<string, ComponentType<unknown>>);

const csrImporters = !import.meta.env.SSR
  ? import.meta.glob<false, string, { default: ComponentType<unknown> }>("@/components/blocks/**/*.tsx")
  : ({} as Record<string, () => Promise<{ default: ComponentType<unknown> }>>);

// Small cache so multiple instances of the same block
// reuse one React.lazy component (prevents duplicate chunk inits)
const lazyCache: Record<string, LazyExoticComponent<ComponentType<unknown>>> = {};

// Blocks preloadElementalBlocks has already resolved, keyed the same way as csrImporters/
// lazyCache. ElementalArea renders straight from here when it can - see the comment below on
// why going through React.lazy()/Suspense at all isn't safe even for an already-resolved import.
const resolvedModules: Record<string, ComponentType<unknown>> = {};

// Lets a block know whether it's the first one rendered on the page (e.g. so it can use an H1)
// without passing an extra prop that blocks spreading their props onto a DOM element would leak.
const IsFirstBlockContext = createContext(false);
export const useIsFirstBlock = () => useContext(IsFirstBlockContext);

const blockPathForTemplate = (template: string | null) => `/themes/default/src/js/components/blocks/${template}.tsx`;

/**
 * SSR renders every block's real markup eagerly, but the client resolves each one as a separate
 * lazy chunk. Preloading the chunk ahead of hydrateRoot (see app.tsx) is not enough on its own:
 * React.lazy() always calls its loader fresh on a component's first render and always suspends on
 * that call, even when the import it kicks off is already settled - a .then() on a resolved
 * promise still only fires on a later microtask, never synchronously during render. Hydrating a
 * whole page hits a lazy boundary for every block at once, and there's enough real work on the
 * main thread between the throw and the retry for the browser to paint the collapsed
 * `fallback={null}` state at least once - a huge, page-wide layout shift.
 *
 * So preloading has to skip React.lazy() entirely rather than just warm it: this stores the
 * resolved component in `resolvedModules`, and ElementalArea renders it directly, with no
 * Suspense boundary at all, whenever it's already there. React.lazy()/Suspense stays only as a
 * fallback for anything this walk didn't reach (e.g. past the depth cap below).
 */
export const preloadElementalBlocks = (data: unknown, seen = new Set<string>(), depth = 0): Promise<unknown> => {
    if (import.meta.env.SSR || depth > 12 || data === null || typeof data !== "object") {
        return Promise.resolve();
    }

    const promises: Promise<unknown>[] = [];

    if (Array.isArray(data)) {
        for (const item of data) {
            promises.push(preloadElementalBlocks(item, seen, depth + 1));
        }
        return Promise.all(promises);
    }

    const record = data as Record<string, unknown>;
    const template = typeof record._template === "string" ? record._template : null;
    if (template) {
        const resolvedTemplate = template === "ElementVirtual" && record.LinkedElement
            ? (record.LinkedElement as Record<string, unknown>)._template
            : template;
        if (typeof resolvedTemplate === "string" && !seen.has(resolvedTemplate)) {
            seen.add(resolvedTemplate);
            const path = blockPathForTemplate(resolvedTemplate);
            const importer = csrImporters[path];
            if (importer) {
                promises.push(importer().then((mod) => {
                    resolvedModules[path] = mod.default;
                }));
            }
        }
    }

    for (const value of Object.values(record)) {
        promises.push(preloadElementalBlocks(value, seen, depth + 1));
    }

    return Promise.all(promises);
};

const ElementalArea = ({ ElementalArea }: { ElementalArea: DNADesignElementalModelsElementalAreaType | null }) => {
    const isCMSPreview = usePage<SharedProps>().props.CMSPreview;
    // Unlinked virtual blocks render nothing, so they can't count as the first block
    const firstIndex = ElementalArea?.Elements?.findIndex((element) =>
        element._template !== "ElementVirtual" || !!(element as DNADesignElementalVirtualModelElementVirtualType).LinkedElement
    ) ?? -1;
    return (
        <>
            {mapArray(ElementalArea?.Elements, (element, index) => (
                <IsFirstBlockContext.Provider key={element._id} value={index === firstIndex}>
                    {renderElement(element, isCMSPreview)}
                </IsFirstBlockContext.Provider>
            ))}
        </>
    );
}

type AreaElement = NonNullable<DNADesignElementalModelsElementalAreaType["Elements"]>[number];

const renderElement = (element: AreaElement, isCMSPreview: boolean | undefined) => {
    if (element._template === "ElementVirtual") {
        const linked = (element as DNADesignElementalVirtualModelElementVirtualType).LinkedElement as DNADesignElementalModelsBaseElementType | null;
        if (!linked) {
            // Unlinked virtual blocks can exist while being edited in CMS; skip rendering to avoid runtime errors.
            return null;
        }
        element = linked as AreaElement;
    }
    // build the props early on, pass these to the components
    const props = {
        id: element.Anchor ?? `e${element._id}`,
        className: `${element.Theme?.CSSClass ?? ''} ${element.ExtraClass ?? ''} ${element.ApplyBackgroundPattern ? 'bg-pattern' : ''} ${element.ApplyLogoBackgroundPattern ? 'logo-bg-pattern' : ''}`,
        block: element,
        ...(isCMSPreview ? { "data-element-id": `${element._id}` } : {}),
    };

    const path = blockPathForTemplate(element._template);
    if (import.meta.env.SSR) {
        const Block = ssrBlocks[path];
        return Block ? <Suspense fallback={null} key={element._id}><Block key={element._id} {...props} /></Suspense> : null;
    }

    // SSR always wraps the block in a Suspense boundary, which streams matching
    // boundary markers into the HTML - the client has to render a Suspense boundary
    // at this same position too or hydration mismatches (even though, unlike
    // LazyBlock below, ResolvedBlock is a plain component that never itself throws a
    // promise, so this boundary never actually suspends).
    const ResolvedBlock = resolvedModules[path];
    if (ResolvedBlock) {
        return (
            <Suspense fallback={null} key={element._id}>
                <ResolvedBlock key={element._id} {...props} />
            </Suspense>
        );
    }

    const importer = csrImporters[path];
    if (!importer) return null;

    if (!lazyCache[path]) {
        lazyCache[path] = lazy(importer);
    }

    const LazyBlock = lazyCache[path];

    return (
        <Suspense fallback={null} key={element._id}>
            <LazyBlock key={element._id} {...props} />
        </Suspense>
    )
}

export default ElementalArea;
